<script setup lang="ts">
import { ref, reactive, computed, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { MAX_VARIABLE_NAME_LENGTH } from 'shared/src'
import type { GlobalVariable, VariableType } from 'shared/src'
import {
  useCreateVariableMutation,
  useUpdateVariableMutation,
} from '@/composables/queries/variables'
import { getVariableTypeOptions } from '@/composables/variables/variableTypeOptions'
import { useToast } from '@ai/ui-components/toast'
import { FetchApiError } from '@/utils/fetchApi'

const { t } = useI18n()
const toast = useToast()

const open = ref(false)
const showCancelConfirmation = ref(false)

const editingVariable = ref<GlobalVariable | null>(null)
const isEditMode = computed(() => editingVariable.value !== null)

const name = ref('')
const type = ref<VariableType | ''>('')
const fallbackValue = ref('')
const description = ref('')
const submitAttempted = ref(false)

const { mutateAsync: createVariable, isPending: isCreatePending } = useCreateVariableMutation()
const { mutateAsync: updateVariable, isPending: isUpdatePending } = useUpdateVariableMutation()
const isPending = computed(() => isCreatePending.value || isUpdatePending.value)

const typeOptions = getVariableTypeOptions(t)

const EMPTY_BOOL_FALLBACK = '__empty__'

const boolOptions = computed(() => [
  { value: 'true', label: 'True' },
  { value: 'false', label: 'False' },
  { value: EMPTY_BOOL_FALLBACK, label: t('variables.fallback_value_empty') },
])

const isBoolType = computed(() => type.value === 'boolean')
const boolFallbackValue = computed({
  get: () => (fallbackValue.value === '' ? EMPTY_BOOL_FALLBACK : fallbackValue.value),
  set: (value: string) => {
    fallbackValue.value = value === EMPTY_BOOL_FALLBACK ? '' : value
  },
})

const NAME_REGEX = /^[a-z][a-z0-9_]*$/

const errors = reactive({
  name: '',
  fallbackValue: '',
})

const clearErrors = () => {
  errors.name = ''
  errors.fallbackValue = ''
}

const validateNameLength = (value: string): string => {
  if (value.length > MAX_VARIABLE_NAME_LENGTH) {
    return t('variables.variable_name_max_length')
  }
  return ''
}

const validateName = (value: string): string => {
  if (!value.trim()) return t('variables.variable_name_required')
  const lengthError = validateNameLength(value)
  if (lengthError) return lengthError
  if (/[A-Z]/.test(value)) return t('variables.variable_name_uppercase')
  if (!NAME_REGEX.test(value)) return t('variables.variable_name_format')
  return ''
}

const validateFallbackValue = (value: string): string => {
  const trimmedValue = value.trim()

  if (!trimmedValue) return ''
  if (type.value === 'int' && !/^-?\d+$/.test(trimmedValue)) {
    return t('variables.fallback_value_invalid_type')
  }
  if (type.value === 'float' && !/^-?\d+([.,]\d+)?$/.test(trimmedValue)) {
    return t('variables.fallback_value_invalid_type')
  }
  return ''
}

const normalizeFallbackValue = (value: string): string => (value.trim() ? value : '')

const hasUnsavedChanges = computed(() => {
  if (isEditMode.value) {
    const orig = editingVariable.value
    if (!orig) return false
    return (
      name.value !== orig.name ||
      type.value !== orig.type ||
      normalizeFallbackValue(fallbackValue.value) !== normalizeFallbackValue(orig.fallbackValue) ||
      description.value !== (orig.description ?? '')
    )
  }
  return (
    name.value !== '' ||
    normalizeFallbackValue(fallbackValue.value) !== '' ||
    description.value !== '' ||
    type.value !== ''
  )
})

const panelOpen = computed({
  get: () => open.value,
  set: (newValue: boolean) => {
    if (!newValue && open.value && hasUnsavedChanges.value) {
      showCancelConfirmation.value = true
      return
    }
    open.value = newValue
  },
})

const resetForm = () => {
  editingVariable.value = null
  name.value = ''
  type.value = ''
  fallbackValue.value = ''
  description.value = ''
  submitAttempted.value = false
  clearErrors()
}

const openPanel = async (variable?: GlobalVariable) => {
  resetForm()
  if (variable) {
    editingVariable.value = variable
    name.value = variable.name
    type.value = variable.type
    description.value = variable.description ?? ''
    await nextTick()
    fallbackValue.value = variable.fallbackValue
  }
  open.value = true
}

const closePanel = () => {
  open.value = false
  resetForm()
  return true
}

const handleCancel = (): boolean => {
  if (hasUnsavedChanges.value) {
    showCancelConfirmation.value = true
    return false
  }
  open.value = false
  resetForm()
  return true
}

const confirmDiscard = (): boolean => {
  showCancelConfirmation.value = false
  open.value = false
  resetForm()
  return true
}

const keepEditing = (): boolean => {
  showCancelConfirmation.value = false
  return true
}

const hasFallbackValueError = (err: Error): boolean => {
  if (err instanceof FetchApiError && err.messages) {
    return err.messages.some((m) => m.toLowerCase().includes('fallbackvalue'))
  }
  try {
    const jsonStart = err.message.indexOf(': {')
    if (jsonStart === -1) return false
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const parsed = JSON.parse(err.message.slice(jsonStart + 2))
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      Array.isArray(parsed.detail) &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      parsed.detail.some((e: { loc?: string[] }) => e.loc?.includes('fallback_value'))
    )
  } catch {
    return false
  }
}

const isVariableNameConflict = (err: Error): boolean => {
  if (err instanceof FetchApiError && err.status === 409) return true
  return err.message.toLowerCase().includes('already exists')
}

const handleSubmit = async () => {
  submitAttempted.value = true

  clearErrors()
  errors.name = validateName(name.value)
  errors.fallbackValue = validateFallbackValue(fallbackValue.value)

  const hasErrors = !type.value || Object.values(errors).some((e) => e !== '')
  if (hasErrors) return false

  const normalizedFallbackValue = normalizeFallbackValue(fallbackValue.value)

  try {
    if (isEditMode.value) {
      const variable = editingVariable.value
      if (!variable) return false
      await updateVariable({
        id: variable.id,
        name: name.value,
        type: type.value as VariableType,
        fallbackValue: normalizedFallbackValue,
        description: description.value || undefined,
      })
      toast.success(t('variables.edit_success'))
    } else {
      await createVariable({
        name: name.value,
        type: type.value as VariableType,
        fallbackValue: normalizedFallbackValue,
        description: description.value || undefined,
      })
      toast.success(t('variables.create_success'))
    }
    closePanel()
  } catch (err) {
    if (!(err instanceof Error)) {
      toast.error(t(isEditMode.value ? 'variables.edit_error' : 'variables.create_error'), {
        title: t('error_occurred'),
      })
      return
    }
    if (isVariableNameConflict(err)) {
      errors.name = t('variables.variable_name_conflict')
      return
    }
    if (hasFallbackValueError(err)) {
      errors.fallbackValue = t('variables.fallback_value_invalid_type')
      return
    }
    toast.error(t(isEditMode.value ? 'variables.edit_error' : 'variables.create_error'), {
      title: t('error_occurred'),
    })
  }
}

watch(name, (val, oldVal) => {
  if (val !== oldVal && val.includes(' ')) {
    name.value = val.replace(/ /g, '_')
    return
  }
  errors.name = val.trim() ? validateName(val) : ''
})

watch(type, () => {
  fallbackValue.value = isBoolType.value ? 'true' : ''
  errors.fallbackValue = ''
})

watch(fallbackValue, (val) => {
  errors.fallbackValue = val.trim() ? validateFallbackValue(val) : ''
})

defineExpose({ openPanel })
</script>

<template>
  <ORightPanel
    teleport
    v-model:open="panelOpen"
    :title="t(isEditMode ? 'variables.edit_title' : 'variables.create_title')"
  >
    <template #default>
      <div class="flex flex-col space-y-4">
        <OInput
          v-model="name"
          required
          :label="t('variables.variable_name')"
          :error-message="errors.name"
          :tooltip-content="t('variables.variable_name_tooltip')"
          :placeholder="t('variables.variable_name_placeholder')"
          dataTestid="variable-name"
        />

        <ODropdown
          v-model="type"
          :options="typeOptions"
          medium
          required
          :filter-title="t('variables.type')"
          :label="t('variables.type')"
          :placeholder="t('variables.type_placeholder')"
          :error-message="submitAttempted && !type ? t('variables.type_required') : ''"
          teleport
          dataTestid="variable-type"
        />

        <ODropdown
          v-if="isBoolType"
          v-model="boolFallbackValue"
          :options="boolOptions"
          medium
          :label="t('variables.fallback_value')"
          :error-message="errors.fallbackValue"
          :tooltip-content="t('variables.fallback_value_tooltip')"
          teleport
          dataTestid="variable-fallback"
        />
        <OInput
          v-else
          v-model="fallbackValue"
          :label="t('variables.fallback_value')"
          :error-message="errors.fallbackValue"
          :tooltip-content="t('variables.fallback_value_tooltip')"
          placeholder="Fallback_value"
          dataTestid="variable-fallback"
        />

        <OTextArea
          v-model="description"
          configurator
          size-auto
          :label="t('description')"
          :tooltip-content="t('variables.description_tooltip')"
          :placeholder="t('variables.description_placeholder')"
          dataTestid="variable-description"
        />
      </div>
    </template>

    <template #footer>
      <OButton
        :label="t('cancel')"
        variant="secondary"
        color="neutral"
        size="L"
        @mousedown.prevent
        @click="handleCancel"
      />
      <OButton
        :label="t(isEditMode ? 'save_changes' : 'save')"
        size="L"
        :loading="isPending"
        @click="handleSubmit"
        backgroundColor="success"
      />
    </template>
  </ORightPanel>

  <OModal
    v-model:open="showCancelConfirmation"
    :title="t('unsaved_changes')"
    :primary-action-title="t('discard_changes')"
    :secondary-action-title="t('continue')"
    :primary-action-promise="confirmDiscard"
    :secondary-action-promise="keepEditing"
    primary-action-enabled
    secondary-action-enabled
    danger
  >
    <template #content>
      <span class="text-neutral-1">{{ t('unsaved_changes_warning') }}</span>
    </template>
  </OModal>
</template>

<style>
[data-testid='variable-fallback-__empty__'] span {
  font-style: italic;
}
</style>
